import { Box, Button, Chip, Container, MenuItem, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import axios, { endpoints } from "src/utils/axios";
import { useSettingsContext } from "src/components/settings";
import { Rental, RentalCustomer, RentalOrganizationSettings, RentalTrailer, RentalTrailerEvent } from "src/models";
import useModelData from "src/hooks/use-model-data";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import Iconify from "src/components/iconify/iconify";
import { SearchCard } from "src/components/search-card";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { useSearch } from "src/hooks/use-search";
import { Constants, rentalTrailerType } from "src/constants";
import { RentalTrailerEventModal } from "../trailers/rental-trailer-event-modal";
import { RentalModal } from "./rental-modal";
import { RentalsTable } from "./rentals-table";
import { RentalStatusStageChip } from "./rental-status";

const EMPTY_ADDRESS = { attention: "", street1: "", street2: "", city: "", state: "", zip: "" };

// filterable stages - the same buckets the status chips show
const STAGE_FILTER_OPTIONS = [
  { value: "needsAttention", label: "Needs Attention" },
  { value: "draft", label: "Draft" },
  { value: "insuranceSent", label: "Insurance Sent" },
  { value: "insuranceReview", label: "Insurance Review Needed" },
  { value: "insuranceConfirmed", label: "Insurance Confirmed" },
  { value: "agreementSent", label: "Agreement Sent" },
  { value: "agreementReview", label: "Agreement Review Needed" },
  { value: "readyForOutbound", label: "Ready for Outbound" },
  { value: "outboundReview", label: "Outbound Review Needed" },
  { value: "active", label: "Active" },
  { value: "returnToComplete", label: "Return to Complete" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const ATTENTION_STAGES = ["insuranceReview", "agreementReview", "outboundReview", "returnToComplete"];

export function emptyRental(): Rental {
  return {
    id: 0,
    active: true,
    organizationId: 0,
    rentalStatus: Constants.RENTAL_STATUS_DRAFT,

    customerId: 0,
    customerCompanyName: "",
    customerFirstName: "",
    customerLastName: "",
    customerAddress: { ...EMPTY_ADDRESS },
    customerPhone: "",
    customerEmail: "",
    customerDotNumber: "",
    customerMcNumber: "",
    customerFeinNumber: "",

    trailerId: 0,
    trailerUnitNumber: "",
    make: "",
    model: "",
    year: "",
    vin: "",
    licensePlateState: "",
    licensePlate: "",
    trailerType: "",

    pdOnCustomerInsurance: false,
    pdFile: "",
    insuranceAttachments: [],
    insuranceUuid: "",
    insurancePdfUrl: "",
    insuranceSentAt: null,
    insuranceSubmittedAt: null,
    insuranceConfirmedAt: null,
    insuranceConfirmedByUserId: 0,
    insuranceRejectionReason: "",
    customerInsuranceDocuments: [],

    agreementName: "",
    choiceSelections: {},
    customValues: {},
    signerFieldValues: {},
    rentalStartTime: null,
    rentalEndTime: null,

    agreementUuid: "",
    agreementContractInfo: { contractPdfUrl: "", appliedSignatures: [] },
    signedAgreementPdfUrl: "",
    agreementSignature: "",
    agreementIdPhotos: [],
    agreementSentAt: null,
    agreementSubmittedAt: null,
    agreementConfirmedAt: null,
    agreementConfirmedByUserId: 0,
    agreementRejectionReason: "",

    outEventId: 0,
    inEventId: 0,
  };
}

type PageModel = {
  rentals?: Rental[];
  rentalTrailers?: RentalTrailer[];
  events?: RentalTrailerEvent[];
  rentalCustomers?: RentalCustomer[];
  rentalOrganizationSettings?: RentalOrganizationSettings;
};

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(
    () => ({
      rentals: axios.get(endpoints.rentals.findAll).then((response) => response.data),
      rentalTrailers: axios.get(endpoints.rentalTrailers.findAll).then((response) => response.data),
      events: axios.get(endpoints.rentalTrailerEvents.findAll).then((response) => response.data),
      rentalCustomers: axios.get(endpoints.rentalCustomers.findAll).then((response) => response.data),
      rentalOrganizationSettings: axios
        .get(endpoints.rentalOrganizationSettings.current)
        .then((response) => response.data),
    }),
    []
  );

  const { data, error, loading, setShouldRefresh } = useModelData<PageModel>(dataLoader);

  const [isRentalOpen, setIsRentalOpen] = useState(false);
  const rentalModel = useModelSavingData<Rental>(endpoints.rentals.update, endpoints.rentals.create);

  const [isEventOpen, setIsEventOpen] = useState(false);
  const eventModel = useModelSavingData<RentalTrailerEvent>(
    endpoints.rentalTrailerEvents.update,
    endpoints.rentalTrailerEvents.create
  );

  const rentals = useMemo(() => data?.rentals || [], [data?.rentals]);

  // the inspection waiting for office review, if any: the yard's outbound to
  // confirm, or a completed return to close out
  const reviewEventFor = useCallback(
    (rental: Rental) =>
      (data?.events || []).find(
        (event) =>
          event.rentalId === rental.id &&
          ((event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT &&
            event.rentalTrailerEventStatus === Constants.RENTAL_TRAILER_EVENT_STATUS_AWAITING_CONFIRMATION) ||
            (event.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN && !event.confirmedAt))
      ),
    [data?.events]
  );

  const statusChip = (rental: Rental) => {
    const reviewEvent = reviewEventFor(rental);
    if (reviewEvent?.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT) {
      return <Chip size="small" color="primary" label="Outbound Review Needed" />;
    }
    if (reviewEvent?.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN) {
      return <Chip size="small" color="primary" label="Return to Complete" />;
    }
    return <RentalStatusStageChip rental={rental} />;
  };

  const stageKey = useCallback(
    (rental: Rental) => {
      if (!rental.active && rental.rentalStatus < Constants.RENTAL_STATUS_ACTIVE) {
        return "cancelled";
      }
      const reviewEvent = reviewEventFor(rental);
      if (reviewEvent?.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_OUT) {
        return "outboundReview";
      }
      if (reviewEvent?.rentalTrailerEvent === Constants.RENTAL_TRAILER_EVENT_TYPE_IN) {
        return "returnToComplete";
      }
      switch (rental.rentalStatus) {
        case Constants.RENTAL_STATUS_DRAFT:
          return "draft";
        case Constants.RENTAL_STATUS_INSURANCE_SENT:
          return "insuranceSent";
        case Constants.RENTAL_STATUS_INSURANCE_SUBMITTED:
          return "insuranceReview";
        case Constants.RENTAL_STATUS_INSURANCE_CONFIRMED:
          return "insuranceConfirmed";
        case Constants.RENTAL_STATUS_AGREEMENT_SENT:
          return "agreementSent";
        case Constants.RENTAL_STATUS_AGREEMENT_SUBMITTED:
          return "agreementReview";
        case Constants.RENTAL_STATUS_AGREEMENT_CONFIRMED:
          return "readyForOutbound";
        case Constants.RENTAL_STATUS_ACTIVE:
          return "active";
        case Constants.RENTAL_STATUS_RETURNED:
          return "returnToComplete";
        default:
          return "completed";
      }
    },
    [reviewEventFor]
  );

  const { searchText, setSearchText, filteredList } = useSearch(
    rentals,
    [
      "trailerUnitNumber",
      "make",
      "model",
      "year",
      "vin",
      "licensePlate",
      "licensePlateState",
      "customerCompanyName",
      "customerFirstName",
      "customerLastName",
      "customerEmail",
      "customerPhone",
      "agreementName",
    ],
    undefined,
    // the type and stage are shown as labels, so they should match too
    (rental) => [
      rentalTrailerType(rental.trailerType)?.label || "",
      ...STAGE_FILTER_OPTIONS.filter((option) => option.value === stageKey(rental)).map(
        (option) => option.label
      ),
    ]
  );

  const [stageFilter, setStageFilter] = useState("All");
  const attentionCount = rentals.filter((rental) =>
    ATTENTION_STAGES.includes(stageKey(rental))
  ).length;

  const stageFiltered = (filteredList || []).filter((rental) => {
    if (stageFilter === "All") {
      return true;
    }
    const key = stageKey(rental);
    if (stageFilter === "needsAttention") {
      return ATTENTION_STAGES.includes(key);
    }
    return key === stageFilter;
  });

  const onNewRental = () => {
    const rental = emptyRental();
    // the library's blank forms come pre-attached; staff can remove or add more
    rental.insuranceAttachments = (data?.rentalOrganizationSettings?.blankForms || [])
      .filter((blankForm) => blankForm.name && blankForm.fileUrl)
      .map((blankForm) => ({
        name: blankForm.name,
        blankUrl: blankForm.fileUrl,
        returnedUrl: "",
        returnedAt: null,
      }));
    rentalModel.set(rental);
    setIsRentalOpen(true);
  };

  const onRentalSelected = (rental: Rental) => {
    const reviewEvent = reviewEventFor(rental);
    if (reviewEvent) {
      eventModel.set({ ...reviewEvent });
      setIsEventOpen(true);
      return;
    }
    rentalModel.set({ ...rental });
    setIsRentalOpen(true);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : "xl"}>
      <PageHeader title="Rentals">
        <Button
          variant="contained"
          color="success"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={onNewRental}
        >
          New Rental
        </Button>
      </PageHeader>

      <DisplayPageData loading={loading} error={error} data={data}>
        <Stack gap={2}>
          <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Rentals">
            <Box>
              <TruxSimpleSelectField value={stageFilter} onChange={(val) => setStageFilter(val)}>
                <MenuItem value="All" selected={stageFilter === "All"}>All</MenuItem>
                {STAGE_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value} selected={stageFilter === option.value}>
                    {option.value === "needsAttention" && attentionCount > 0
                      ? `${option.label} (${attentionCount})`
                      : option.label}
                  </MenuItem>
                ))}
              </TruxSimpleSelectField>
            </Box>
          </SearchCard>

          {stageFiltered.length === 0 ? (
            <NoneFound
              title="No Rentals"
              message={
                stageFilter !== "All"
                  ? "No rentals match the selected stage"
                  : "Start a rental by sending the customer an insurance request"
              }
            />
          ) : (
            <RentalsTable
              rentals={stageFiltered}
              onRentalSelected={onRentalSelected}
              statusChip={statusChip}
            />
          )}
        </Stack>

        <RentalModal
          rentalModel={rentalModel}
          rentals={rentals}
          rentalTrailers={data?.rentalTrailers || []}
          rentalCustomers={data?.rentalCustomers || []}
          organizationSettings={data?.rentalOrganizationSettings}
          isOpen={isRentalOpen}
          setIsOpen={setIsRentalOpen}
          onSaved={() => {
            setShouldRefresh(true);
          }}
        />

        <RentalTrailerEventModal
          eventModel={eventModel}
          rentalCustomers={data?.rentalCustomers || []}
          isOpen={isEventOpen}
          setIsOpen={setIsEventOpen}
          organizationSettings={data?.rentalOrganizationSettings}
          onSaved={() => {
            setShouldRefresh(true);
          }}
        />
      </DisplayPageData>
    </Container>
  );
}
